import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import moment from "moment-timezone";

import { useAppDispatch } from "../../../redux/useDispatch";
import { RootState } from "../../../redux/store";
import {
  getSUPAvailableData,
  getSUPAvailableDates,
  getSUPAvailableTimeSlots,
} from "../util/orderDataStepHelper";
import {
  dateSelected,
  durationSelected,
  quantitySelected,
  timeSelected,
} from "../../../redux/slice/bookingCreation/bookingCreationSlice";

import BookingDatePicker from "../common/components/OrderDataStep/BookingDatePicker";
import BookingTimeSelect from "../common/components/OrderDataStep/BookingTimeSelect";
import BookingDurationSelect from "../common/components/OrderDataStep/BookingDurationSelect";
import BookingQuantitySelect from "../common/components/OrderDataStep/BookingQuantitySelect";
import LoaderOverlay from "../../layout/components/LoaderOverlay";
import { useBookingWindow } from "../common/hooks/useBookingWindow";
import { useSelectedLocationTimeZone } from "../../../util/locationTimeZone";

const OrderDataStep = () => {
  const { t } = useTranslation();
  // The tenant's configured booking horizon for this use case — the picker
  // used to cap dates at a hardcoded 18 months, which contradicted the one
  // month the availability request already asked for. One month is the
  // default here, so the effective behaviour is unchanged.
  const bookingWindow = useBookingWindow("sup");
  const dispatch = useAppDispatch();

  // The station's clock (#3219): slots are keyed in the location's zone.
  const timeZone = useSelectedLocationTimeZone();
  const groupAvailabilities = useSelector(
    (state: RootState) => state.bookingCreation.supAvailabilities
  );
  const orderData = useSelector(
    (state: RootState) => state.bookingCreation.orderData
  );

  // Still loading only while nothing has arrived. An answer with no board left
  // is sold out, not loading: the loader used to cover the message (#2652).
  const loadFailed = useSelector(
    (state: RootState) => state.bookingCreation.availabilitiesFailed
  );
  const isLoading = groupAvailabilities === null && !loadFailed;
  const hasAvailabilities = Boolean(groupAvailabilities);

  const availableDates = useMemo<string[]>(
    () => getSUPAvailableDates(groupAvailabilities),
    [groupAvailabilities]
  );
  const soldOut = !isLoading && availableDates.length === 0;

  const availableTimeSlots = useMemo<string[]>(() => {
    if (!groupAvailabilities || !orderData.date) return [];
    return getSUPAvailableTimeSlots(groupAvailabilities, orderData.date, timeZone);
  }, [groupAvailabilities, orderData.date, timeZone]);

  const { allDurations, allPriceMap, allLimitMap } = useMemo(() => {
    if (!groupAvailabilities || !orderData.date || !orderData.time) {
      return {
        allDurations: [] as number[],
        allPriceMap: {} as Record<number, number>,
        allLimitMap: {} as Record<number, number>,
      };
    }
    const { durations, priceMap, limitsMap } = getSUPAvailableData(
      groupAvailabilities,
      orderData.date,
      orderData.time,
      timeZone
    );
    return {
      allDurations: durations ?? [],
      allPriceMap: priceMap ?? {},
      allLimitMap: limitsMap ?? {},
    };
  }, [groupAvailabilities, orderData.date, orderData.time, timeZone]);

  const enableTimeSelect = hasAvailabilities && Boolean(orderData.date);
  const enableDurationSelect =
    hasAvailabilities && Boolean(orderData.date) && orderData.time !== "";
  const enableQuantitySelect =
    enableDurationSelect && Boolean(orderData.duration);

  const handleDateChange = (date: moment.Moment | null) => {
    dispatch(dateSelected({ date }));
  };

  const handleTimeChange = (time: string) => {
    dispatch(timeSelected({ time }));
  };

  return (
    <>
      {loadFailed && (
        <div className="px-4">
          <p role="alert" className="text-base text-slate-900">
            {t("book_location.order_data.error.availabilities_load_failed")}
          </p>
        </div>
      )}
      {!loadFailed && hasAvailabilities &&
        (soldOut ? (
          <div className="px-4">
            <p className="text-base text-slate-900">
              {t("book_location.order_data.error.no_availabilities_for_group")}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4 mx-4">
            <BookingDatePicker
              value={orderData.date}
              onChange={handleDateChange}
              shouldDisableDate={() => false}
              availableDates={availableDates}
              maxDate={bookingWindow.latest}
              label={t("general.date")}
            />
            <BookingTimeSelect
              availableSlots={availableTimeSlots}
              value={orderData.time}
              onChange={handleTimeChange}
              availableDurations={allDurations}
              disabled={!enableTimeSelect}
              label={t("general.select")}
            />
            <BookingDurationSelect
              value={orderData.duration}
              onChange={(duration) =>
                dispatch(durationSelected({ duration }))
              }
              availableDurations={allDurations}
              disabled={!enableDurationSelect}
              prices={allPriceMap}
              label={t("general.duration")}
            />
            <BookingQuantitySelect
              value={orderData.quantity}
              onChange={(quantity) =>
                dispatch(quantitySelected({ quantity }))
              }
              limit={allLimitMap[orderData.duration] ?? 0}
              disabled={!enableQuantitySelect}
              label={t("general.quantity")}
            />
          </div>
        ))}
      <LoaderOverlay open={isLoading} />
    </>
  );
};

export default OrderDataStep;
